/**
 * Shared reading of a mermaid fence body for the in-app chart renderers:
 * the optional YAML front matter block (mermaid's config syntax) and the
 * colour palette an author may set inside it.
 */

export interface MermaidSourceParts {
  /** The front matter block text without its `---` fences (indentation kept), or null. */
  readonly frontMatter: string | null;
  /** Non-empty, non-comment body lines after the front matter, trimmed. */
  readonly lines: ReadonlyArray<string>;
  /** True when a front matter block was opened but never closed. */
  readonly unterminated: boolean;
}

export function splitMermaidSource(code: string): MermaidSourceParts {
  const raw = code.split("\n");
  const lines = raw.map((line) => line.trim());
  let start = 0;
  while (start < lines.length && lines[start]?.length === 0) start += 1;
  let frontMatter: string | null = null;
  if (lines[start] === "---") {
    const end = lines.indexOf("---", start + 1);
    if (end === -1) return { frontMatter: null, lines: [], unterminated: true };
    // Keep the block's indentation: readers nest by it, as YAML does.
    frontMatter = raw.slice(start + 1, end).join("\n");
    start = end + 1;
  }
  return {
    frontMatter,
    lines: lines.slice(start).filter((line) => line.length > 0 && !line.startsWith("%%")),
    unterminated: false,
  };
}

/** Colours end up as SVG fill attributes, so only plain colour syntax passes. */
const COLOR_REGEX = /^(?:#[0-9a-f]{3,8}|[a-z]{3,30}|(?:rgb|hsl|oklch|oklab)a?\([^()]{1,60}\))$/i;

export function isPlainCssColor(value: string): boolean {
  return COLOR_REGEX.test(value.trim());
}

/**
 * Series colours from mermaid's own theme variables, so a palette written for
 * mermaid also drives the interactive chart:
 *   xyChart.plotColorPalette: "#29c0d3, #3337bc"   (xy charts)
 *   pie1: "#29c0d3" … pie12                        (pie charts)
 * Pie slots keep their position — `pie2` alone yields `[null, "#…"]` — so a
 * colour never shifts onto the wrong slice. Null when nothing usable was set.
 */
export type FigurePalette = ReadonlyArray<string | null>;

/** The author's colour for a slot, else the default for that slot. */
export function paletteColor(
  palette: FigurePalette | null,
  index: number,
  defaults: ReadonlyArray<string>,
): string {
  const fallback = defaults[index % defaults.length] ?? defaults[0]!;
  if (!palette || palette.length === 0) return fallback;
  return palette[index] ?? palette[index % palette.length] ?? fallback;
}

export function paletteFromFrontMatter(
  frontMatter: string | null,
  kind: "xy" | "pie",
): FigurePalette | null {
  if (frontMatter == null) return null;
  if (kind === "xy") {
    const match = frontMatter.match(/plotColorPalette\s*:\s*["']?([^"'\n]+)["']?/);
    if (!match?.[1]) return null;
    // Split on commas outside parentheses so `rgb(255, 0, 0)` stays whole.
    const colors = (match[1].match(/[^,()]+(?:\([^()]*\))?/g) ?? [])
      .map((color) => color.trim())
      .filter(isPlainCssColor);
    return colors.length > 0 ? colors : null;
  }
  const colors: Array<string | null> = [];
  for (const match of frontMatter.matchAll(/\bpie(\d{1,2})\s*:\s*["']?([^"'\n]+)["']?/g)) {
    const index = Number(match[1]);
    const color = match[2]?.trim() ?? "";
    if (index >= 1 && index <= 12 && isPlainCssColor(color)) {
      while (colors.length < index - 1) colors.push(null);
      colors[index - 1] = color;
    }
  }
  return colors.some((color) => color != null) ? colors : null;
}
